import ReplayOutlined from "@mui/icons-material/ReplayOutlined";
import ActionSend from "assets/img/svg/actionSend";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { PortalType } from "contexts/portal.context";
import type { SetStateAction } from "react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { resendWelcomeEmail } from "store/adminPanelSlice";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

interface IPortalAccountSection {
    /**
     * Data of the beneficiary.
     */
    beneficiary: IBeneficiar;
    /**
     * Triggers opening modal to invite beneficiary;
     */
    setModalVariant: (value: SetStateAction<"delete" | "access" | "create" | "edit" | "deleteRep" | null>) => void;
}

enum EAccountStatus {
    NO_ACCOUNT = "noaccount",
    INVITED = "pending",
    ACTIVE = "confirmed",
}

/**
 * On Beneficiary page, section which display status of Portal Account: pending, confirmed and if not, it shows a button to invite.
 */
const PortalAccountSection = ({ beneficiary, setModalVariant }: IPortalAccountSection) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);

    const [disableResend, setDisableResend] = useState(false);

    const beneficiaryAccountStatus: Record<EAccountStatus, IStatusRule> = {
        [EAccountStatus.ACTIVE]: { color: "green", text: t("Beneficiars.Activated") },
        [EAccountStatus.INVITED]: { color: "yellow", text: t("Beneficiars.Awaiting") },
        [EAccountStatus.NO_ACCOUNT]: { color: "red", text: t("Beneficiars.Inactive") },
    };

    const invitePending = beneficiary.accountStatus === EAccountStatus.INVITED;

    const handleResendingInvitation = async () => {
        if (disableResend) return;
        setDisableResend(true);
        await dispatch(resendWelcomeEmail({ email: beneficiary.email }));
        setTimeout(() => {
            setDisableResend(false);
        }, 5000);
    };

    return (
        <div className={styles["beneficiar-portal-access"]}>
            <div className={styles["beneficiar-portal-access-content"]}>
                <div className={styles["beneficiar-portal-access-title"]}>
                    {t("Beneficiars.BeneficiarPortal")}
                    <StatusChip status={beneficiary.accountStatus} statusRules={beneficiaryAccountStatus} />
                    {invitePending && (
                        <ReplayOutlined
                            sx={{
                                color: "#676767",
                                fontSize: "1rem",
                                cursor: disableResend ? "not-allowed" : "pointer",
                                opacity: disableResend ? "0.4" : "1",
                            }}
                            onClick={() => handleResendingInvitation()}
                        />
                    )}
                </div>
                <div className={styles["beneficiar-portal-access-text"]}>
                    {beneficiary.accountStatus === EAccountStatus.NO_ACCOUNT
                        ? t(`Beneficiars.BeneficiarPortalText_${portalType}`, {
                              name: `${beneficiary.name ?? ""} ${beneficiary.surname}`,
                          })
                        : t("Beneficiars.BeneficiarPortalTextDone", {
                              name: `${beneficiary.name ?? ""} ${beneficiary.surname}`,
                              mail: beneficiary.loginEmail ?? beneficiary.email ?? "???",
                          })}
                </div>
            </div>
            {beneficiary.accountStatus === EAccountStatus.NO_ACCOUNT && (
                <div className={styles["beneficiar-portal-access-action"]}>
                    <StyledButton
                        sx={{
                            gap: "8px",
                            height: "36px",
                            padding: "8px 16px",
                            borderColor: "#141516 !important",
                        }}
                        onClick={() => setModalVariant("access")}
                        buttonType="secondary"
                    >
                        <ActionSend />
                        {t("Beneficiars.GiveAccess")} {beneficiary.name ?? ""} {beneficiary.surname}
                    </StyledButton>
                </div>
            )}
        </div>
    );
};

export default PortalAccountSection;
